import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import client, { mensajeDeError } from '../../api/client';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { MensajeError, MensajeExito } from '../../components/ui/Form';
import { fechaCompleta, horaLegible, desdeISO } from '../../utils/fechas';

export const ListaReservas = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

export const Reserva = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};

  h3 {
    font-size: 1.1rem;
  }

  p {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

export const Detalle = styled.div`
  flex: 1;
  min-width: 200px;
`;

export const Vacio = styled(Card)`
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};

  a {
    color: ${({ theme }) => theme.colors.sageDeep};
    font-weight: 600;
    text-decoration: underline;
  }
`;

const Aviso = styled.p`
  font-size: 0.92rem;
  margin-bottom: ${({ theme }) => theme.space.md};

  strong {
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const Acciones = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  justify-content: flex-end;
`;

const QrImagen = styled.img`
  display: block;
  width: 220px;
  height: 220px;
  margin: 0 auto ${({ theme }) => theme.space.md};
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const QrTexto = styled.p`
  text-align: center;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.space.md};
`;

function horasParaClase(reserva) {
  const inicio = new Date(`${reserva.fecha}T${reserva.hora_inicio}`);
  return (inicio - new Date()) / 36e5;
}

function MisReservas() {
  const [reservas, setReservas] = useState(null);
  const [porCancelar, setPorCancelar] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  const [qrDe, setQrDe] = useState(null);
  const [qrImagen, setQrImagen] = useState('');
  const [qrError, setQrError] = useState('');
  const [cargandoQr, setCargandoQr] = useState(false);

  const cargar = useCallback(() => {
    client
      .get('/reservas/mias')
      .then(({ data }) => setReservas(data))
      .catch(() => setReservas([]));
  }, []);

  useEffect(cargar, [cargar]);

  const confirmarCancelacion = async () => {
    setCancelando(true);
    setError('');
    try {
      const { data } = await client.delete(`/reservas/${porCancelar.id}`);
      setExito(data.mensaje);
      setPorCancelar(null);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setCancelando(false);
    }
  };

  const verQr = async (reserva) => {
    setQrError('');
    setQrImagen('');
    setQrDe(reserva);
    setCargandoQr(true);
    try {
      const { data } = await client.get(`/reservas/${reserva.id}/qr`);
      setQrImagen(data.qr);
    } catch (err) {
      setQrError(mensajeDeError(err));
    } finally {
      setCargandoQr(false);
    }
  };

  if (!reservas) return <Spinner />;

  return (
    <>
      {exito && <MensajeExito role="status">{exito}</MensajeExito>}
      {error && !porCancelar && <MensajeError role="alert">{error}</MensajeError>}

      {reservas.length === 0 ? (
        <Vacio>
          No tienes clases reservadas. <Link to="/agenda">Explora la agenda</Link> y aparta tu
          lugar.
        </Vacio>
      ) : (
        <ListaReservas>
          {reservas.map((r) => (
            <Reserva key={r.id}>
              <Detalle>
                <h3>{r.tipo_clase}</h3>
                <p>
                  {fechaCompleta(r.fecha)} · {horaLegible(r.hora_inicio)} · {r.instructor}
                </p>
              </Detalle>
              <Badge $tono={desdeISO(r.fecha).getTime() - Date.now() < 864e5 ? 'alerta' : 'sage'}>
                {horasParaClase(r) >= 12 ? 'Cancelación sin costo' : 'Menos de 12 h'}
              </Badge>
              <Button $variante="secundario" $chico onClick={() => verQr(r)}>
                Ver código QR
              </Button>
              <Button $variante="peligro" $chico onClick={() => { setError(''); setPorCancelar(r); }}>
                Cancelar
              </Button>
            </Reserva>
          ))}
        </ListaReservas>
      )}

      <Modal abierto={!!porCancelar} titulo="Cancelar reserva" onCerrar={() => setPorCancelar(null)}>
        {porCancelar && (
          <>
            <Aviso>
              Vas a cancelar <strong style={{ color: 'inherit' }}>{porCancelar.tipo_clase}</strong>{' '}
              del {fechaCompleta(porCancelar.fecha)} a las {horaLegible(porCancelar.hora_inicio)}.
            </Aviso>
            {horasParaClase(porCancelar) >= 12 ? (
              <Aviso>La clase regresará a tu paquete porque cancelas con más de 12 horas de anticipación.</Aviso>
            ) : (
              <Aviso>
                <strong>Faltan menos de 12 horas para la clase:</strong> si cancelas ahora, la
                clase se descuenta de tu paquete y no se repone.
              </Aviso>
            )}
            {error && <MensajeError role="alert">{error}</MensajeError>}
            <Acciones>
              <Button $variante="fantasma" onClick={() => setPorCancelar(null)}>
                Conservar reserva
              </Button>
              <Button $variante="peligroSolido" onClick={confirmarCancelacion} disabled={cancelando}>
                {cancelando ? 'Cancelando…' : 'Sí, cancelar'}
              </Button>
            </Acciones>
          </>
        )}
      </Modal>

      <Modal abierto={!!qrDe} titulo="Código de entrada" onCerrar={() => setQrDe(null)}>
        {qrDe && (
          <>
            <QrTexto>
              {qrDe.tipo_clase} · {fechaCompleta(qrDe.fecha)} · {horaLegible(qrDe.hora_inicio)}
            </QrTexto>
            {cargandoQr && <Spinner />}
            {qrError && <MensajeError role="alert">{qrError}</MensajeError>}
            {qrImagen && (
              <>
                <QrImagen src={qrImagen} alt="Código QR de entrada a la clase" />
                <QrTexto>Muéstralo en recepción para registrar tu entrada a la clase.</QrTexto>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
}

export default MisReservas;
